import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpEventType} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {environment} from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class TrustiiService {
  urlV2 = environment.apiV2url;
  withCredentials = false;

  constructor(private http: HttpClient) {
  }

  testJupyter(url): Observable<any> {
    const token = localStorage.getItem('token');
    const httpOptions = {
      headers: new HttpHeaders(
        {'Content-Type': 'application/json', Authorization: 'bearer ' + token}),
      withCredentials: this.withCredentials,
    };

    return this.http.post<any>(url, httpOptions);
  }

  getDatasets(name: string, page: number = 1, perPage: number = 20): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'}),
      withCredentials: this.withCredentials,
    };

    let queryString = '?page=' + page + '&per_page=' + perPage;
    queryString += ('' !== name.trim()) ? '&name=' + this.urlEncode(name.trim()) : '';
    queryString += '&type=' + this.urlEncode('training');

    return this.http.get<any>(this.urlV2 + '/enterprise/datasets' + queryString, httpOptions);
  }

  getUniversities(page: number = 1, perPage: number = 100): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'}),
      withCredentials: this.withCredentials,
    };

    const queryString = '?page=' + page + '&per_page=' + perPage;
    return this.http.get<any>(this.urlV2 + '/enterprise/datasets/config/universities' + queryString, httpOptions);
  }

  getMlModels(datasetName: string, datasetId: number, page: number = 1, perPage: number = 20): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'}),
      withCredentials: this.withCredentials,
    };

    let queryString = '?page=' + page + '&per_page=' + perPage;
    queryString += ('' !== datasetName.trim()) ? '&dataset_name=' + this.urlEncode(datasetName.trim()) : '';
    queryString += (datasetId > 0) ? '&dataset_id=' + datasetId : '';
    return this.http.get<any>(this.urlV2 + '/enterprise/mlmodels' + queryString, httpOptions);
  }

  getInsights(datasetName: string, datasetId: number, page: number = 1, perPage: number = 20): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'}),
      withCredentials: this.withCredentials,
    };

    let queryString = '?page=' + page + '&per_page=' + perPage;
    queryString += ('' !== datasetName.trim()) ? '&dataset_name=' + this.urlEncode(datasetName.trim()) : '';
    queryString += (datasetId > 0) ? '&dataset_id=' + datasetId : '';

    return this.http.get<any>(this.urlV2 + '/enterprise/insights' + queryString, httpOptions);
  }

  getUsers(page: number = 1, perPage: number = 20): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'}),
      withCredentials: this.withCredentials,
    };

    const queryString = '?page=' + page + '&per_page=' + perPage;
    return this.http.get<any>(this.urlV2 + '/users' + queryString, httpOptions);
  }

  getUserNotebook(): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'}),
      withCredentials: this.withCredentials,
    };
    return this.http.get<any>(this.urlV2 + '/users/notebook', httpOptions);
  }

  inviteUser(payload): Observable<any> {
    const httpOptions = {
      withCredentials: this.withCredentials,
    };
    return this.http.post<any>(this.urlV2 + '/users/invite', payload, httpOptions);
  }

  updateUser(id, payload): Observable<any> {
    const httpOptions = {
      withCredentials: this.withCredentials,
    };
    return this.http.patch<any>(this.urlV2 + '/users/' + id, payload, httpOptions);
  }

  updateDataset(payload, datasetId): Observable<any> {
    const httpOptions = {
      withCredentials: this.withCredentials,
    };
    return this.http.patch<any>(this.urlV2 + '/enterprise/datasets/' + datasetId, payload, httpOptions);
  }

  deleteDataset(datasetId): Observable<any> {
    const httpOptions = {
      withCredentials: this.withCredentials,
    };
    return this.http.delete<any>(this.urlV2 + '/enterprise/datasets/' + datasetId, httpOptions);
  }

  buildMLModel(datasetId, type = 'auto'): Observable<any> {
    const httpOptions = {
      withCredentials: this.withCredentials
    };
    return this.http.post<any>(this.urlV2 + '/enterprise/mlmodels/' + type,
      {datasetId, useAsync: 1}, httpOptions);
  }

  getMLModel(modelId): Observable<any> {
    const httpOptions = {
      withCredentials: this.withCredentials,
    };
    return this.http.get<any>(this.urlV2 + '/enterprise/mlmodels/' + modelId,
      httpOptions);
  }

  downloadMLModel(modelId): Observable<any> {
    return this.http.get<any>(this.urlV2 + '/enterprise/mlmodels/' + modelId + '/file',
      {
        responseType: 'blob' as 'json',
        headers: new HttpHeaders({'Content-Type': 'application/text'}),
        withCredentials: this.withCredentials,
        reportProgress: true,
        observe: 'events',
      }).pipe(map((event) => {

        switch (event.type) {

          case HttpEventType.DownloadProgress:
            const progress = Math.round(100 * event.loaded / event.total);
            return {status: 'progress', message: progress};

          case HttpEventType.Response:
            return event.body;
          default:
            return `Unhandled event: ${event.type}`;
        }
      })
    );
  }

  uploadTerms(data, datasetId) {
    return this.http.post<any>(this.urlV2 + '/enterprise/datasets/' + datasetId + '/terms', data, {
      withCredentials: this.withCredentials,
    });
  }

  runMLModel(datasetId, modelId, mode = 'class', inputDataset = []): Observable<any> {
    const httpOptions = {
      withCredentials: this.withCredentials,
    };
    return this.http.post<any>(this.urlV2 + '/enterprise/mlmodels/' + modelId + '/run',
      {datasetId, inputDataset, mode}, httpOptions);
  }

  getInsightDetails(insightId): Observable<any> {
    const httpOptions = {
      withCredentials: this.withCredentials,
    };
    return this.http.get<any>(this.urlV2 + '/enterprise/insights/' + insightId,
      httpOptions);
  }

  getInsightData(insightId, toDelete = false): Observable<any> {
    const httpOptions = {
      withCredentials: this.withCredentials,
    };
    let url = this.urlV2 + '/enterprise/insights/' + insightId + '/data';
    if (toDelete) {
      url = url + '&to_delete=1';
    }
    return this.http.get<any>(url,
      httpOptions);
  }

  download(): Observable<any> {
    const httpOptions = {
      responseType: 'blob' as 'json',
      headers: new HttpHeaders({'Content-Type': 'application/text'})
    };
    return this.http.get<any>('http://localhost:3000/download', httpOptions);
  }

  public upload(data, type) {
    return this.http.post<any>(this.urlV2 + '/enterprise/datasets/' + type, data, {
      withCredentials: this.withCredentials,
      reportProgress: true,
      observe: 'events',
    }).pipe(map((event) => {

        switch (event.type) {

          case HttpEventType.UploadProgress:
            const progress = Math.round(100 * event.loaded / event.total);
            return {status: 'progress', message: progress, uploaded: event.loaded};

          case HttpEventType.Response:
            return event.body;
          default:
            return `Unhandled event: ${event.type}`;
        }
      })
    );
  }

  login(username, password): Observable<any> {
    const loginData = {
      auth_username: username,
      auth_password: password,
    };
    const httpOptions = {
      withCredentials: this.withCredentials,
    };
    return this.http.post<any>(this.urlV2 + '/users/login',
      loginData, httpOptions);
  }

  logout(): Observable<any> {
    const httpOptions = {
      withCredentials: this.withCredentials,
    };
    return this.http.post<any>(this.urlV2 + '/users/logout',
      {}, httpOptions);
  }

  resetPassword(oldPassword, newPassword, repeatNewPassord, firstname, lastname): Observable<any> {
    const resetData = {
      oldPassword,
      newPassword,
      repeatNewPassword: repeatNewPassord,
      firstname,
      lastname
    };
    const httpOptions = {
      withCredentials: this.withCredentials,
    };
    return this.http.patch<any>(this.urlV2 + '/users/password/change',
      resetData, httpOptions);
  }

  autoLogin(): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'}),
      withCredentials: this.withCredentials,
    };
    return this.http.get<any>(this.urlV2 + '/users/autologin', httpOptions);
  }

  getDataset(datasetId): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'}),
      withCredentials: this.withCredentials,
    };
    return this.http.get<any>(this.urlV2 + '/datasets/' + datasetId, httpOptions);
  }

  vendorParticipateDataset(datasetId): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'}),
      withCredentials: this.withCredentials,
    };
    return this.http.post<any>(this.urlV2 + '/vendor/datasets/' + datasetId + '/participate', httpOptions);
  }

  vendorAcceptCondition(datasetId): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'}),
      withCredentials: this.withCredentials,
    };
    return this.http.patch<any>(this.urlV2 + '/vendor/datasets/' + datasetId + '/conditions', httpOptions);
  }

  vendorConditionToAccept(): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'}),
      withCredentials: this.withCredentials,
    };

    return this.http.get<any>(this.urlV2 + '/vendor/datasets/changed/conditions', httpOptions);
  }

  searchDatasets(options, page: number = 1, perPage: number = 20): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'}),
      withCredentials: this.withCredentials,
    };

    let queryString = '?page=' + page + '&per_page=' + perPage;
    queryString += ('' !== options.name.trim()) ? '&name=' + this.urlEncode(options.name.trim()) : '';
    queryString += '&type=' + this.urlEncode('training');

    return this.http.get<any>(this.urlV2 + '/vendor/datasets' + queryString, httpOptions);
  }

  getDatasetFile(datasetId, part): Observable<any> {
    const httpOptions = {
      withCredentials: this.withCredentials,
      responseType: 'blob' as 'json',
      headers: new HttpHeaders({'Content-Type': 'application/json'}),
    };

    return this.http.get<any>(
      this.urlV2 + '/vendor/datasets/' + datasetId + '/file?part=' + part,
      httpOptions
    );
  }

  getDatasetDownloadLink(datasetId, part): string {
    const token = localStorage.getItem('token');
    return this.urlV2 + '/vendor/datasets/' + datasetId + '/file?part=' + part + '&trustiiApiUserToken=' + token;
  }

  getDatasetTerms(datasetId, responseType = null): Observable<any> {
    const httpOptions = {
      withCredentials: this.withCredentials,
      responseType: 'blob' as 'json',
      headers: new HttpHeaders({'Content-Type': 'application/json'}),
    };

    return this.http.get<any>(
      this.urlV2 + '/datasets/' + datasetId + '/terms',
      httpOptions
    );
  }

  getBaseline(datasetId: number): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'}),
      withCredentials: this.withCredentials,
    };
    return this.http.get<any>(this.urlV2 + '/datasets/' + datasetId + '/baseline', httpOptions);
  }

  getVendorPredictions(datasetId: number, page: number = 1, perPage: number = 20, listVendor = false): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'}),
      withCredentials: this.withCredentials,
    };

    let queryString = '?page=' + page + '&per_page=' + perPage;
    queryString += (datasetId > 0) ? '&dataset_id=' + datasetId : '';

    return this.http.get<any>(this.urlV2 + '/vendor/predictions' + queryString, httpOptions);
  }

  getVendorDatasetTeam(datasetId: number): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'}),
      withCredentials: this.withCredentials,
    };
    return this.http.get<any>(this.urlV2 + '/vendor/teams/datasets/' + datasetId, httpOptions);
  }

  updateTeam(payload, datasetId): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'}),
      withCredentials: this.withCredentials,
    };
    return this.http.patch<any>(this.urlV2 + '/vendor/teams/datasets/' + datasetId, payload, httpOptions);
  }

  updateInvitation(payload, datasetId, id): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'}),
      withCredentials: this.withCredentials,
    };
    return this.http.patch<any>(
      this.urlV2 + '/vendor/teams/datasets/' + datasetId + '/invitations/' + id,
      payload,
      httpOptions
    );
  }

  inviteTeamMember(payload, datasetId): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'}),
      withCredentials: this.withCredentials,
    };
    return this.http.post<any>(this.urlV2 + '/vendor/teams/datasets/' + datasetId + '/invite', payload, httpOptions);
  }

  datasetTeamsMember(datasetId): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'}),
      withCredentials: this.withCredentials,
    };
    return this.http.get<any>(this.urlV2 + '/vendor/teams/datasets/' + datasetId + '/teams', httpOptions);
  }

  datasetTeamRequestsMember(datasetId): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'}),
      withCredentials: this.withCredentials,
    };
    return this.http.get<any>(this.urlV2 + '/vendor/teams/datasets/' + datasetId + '/requests', httpOptions);
  }

  datasetTeamInvitations(datasetId): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'}),
      withCredentials: this.withCredentials,
    };
    return this.http.get<any>(this.urlV2 + '/vendor/teams/datasets/' + datasetId + '/invitations', httpOptions);
  }

  getLeaderboard(datasetId: number, page: number = 1, perPage: number = 20, orderBy: string = 'defaultPublicScore'): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'}),
      withCredentials: this.withCredentials,
    };

    const queryString = '?page=' + page + '&per_page=' + perPage + '&order_by=' + orderBy;
    return this.http.get<any>(this.urlV2 + '/datasets/' + datasetId + '/leaderboard' + queryString, httpOptions);
  }

  createPrediction(data, datasetId) {
    return this.http.post<any>(this.urlV2 + '/ds/notebook/datasets/' + datasetId + '/prediction?async=yes', data, {
      reportProgress: true,
      withCredentials: this.withCredentials,
      observe: 'events',
    }).pipe(map((event) => {

        switch (event.type) {
          case HttpEventType.UploadProgress:
            const progress = Math.round(100 * event.loaded / event.total);
            return {status: 'progress', message: progress};

          case HttpEventType.Response:
            return event.body;
          default:
            return `Unhandled event: ${event.type}`;
        }
      })
    );
  }

  updatePrediction(predictionId, payload): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'}),
      withCredentials: this.withCredentials,
    };
    return this.http.patch<any>(this.urlV2 + '/vendor/predictions/' + predictionId, payload, httpOptions);
  }

  getVendorPrediction(predictionId): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'}),
      withCredentials: this.withCredentials,
    };
    return this.http.get<any>(this.urlV2 + '/vendor/predictions/' + predictionId, httpOptions);
  }

  getPredictionNotebookLink(id: number): string {
    const token = localStorage.getItem('token');
    return this.urlV2 + '/vendor/predictions/' + id + '/notebook?trustiiApiUserToken=' + token;
  }

  getDatasetToken(datasetId: number): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'}),
      withCredentials: this.withCredentials,
    };

    return this.http.get<any>(this.urlV2 + '/vendor/datasets/' + datasetId + '/notebook', httpOptions);
  }

  support(payload): Observable<any> {
    const httpOptions = {
      withCredentials: this.withCredentials,
    };
    return this.http.post<any>(this.urlV2 + '/users/support', payload, httpOptions);
  }

  signup(payload): Observable<any> {
    const httpOptions = {
      withCredentials: this.withCredentials,
    };
    return this.http.post<any>(this.urlV2 + '/users/signup', payload, httpOptions);
  }

  askResetPassword(payload): Observable<any> {
    const httpOptions = {
      withCredentials: this.withCredentials,
    };
    return this.http.post<any>(this.urlV2 + '/users/password/reset', payload, httpOptions);
  }

  confirmResetPassword(payload): Observable<any> {
    const httpOptions = {
      withCredentials: this.withCredentials,
    };
    return this.http.post<any>(this.urlV2 + '/users/password/confirm', payload, httpOptions);
  }

  updateDatasetAccessList(payload, datasetId): Observable<any> {
    const httpOptions = {
      withCredentials: this.withCredentials,
    };
    return this.http.post<any>(this.urlV2 + '/enterprise/datasets/' + datasetId + '/access', payload, httpOptions);
  }


  getDatasetAccessList(datasetId): Observable<any> {
    const httpOptions = {
      withCredentials: this.withCredentials,
    };
    return this.http.get<any>(this.urlV2 + '/enterprise/datasets/' + datasetId + '/access', httpOptions);
  }

  getStats(datasetId): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'}),
      withCredentials: this.withCredentials,
    };
    return this.http.get<any>(this.urlV2 + '/datasets/' + datasetId + '/stats', httpOptions);
  }

  getTeamMembers(datasetId: number, teamId: number): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'}),
      withCredentials: this.withCredentials,
    };
    return this.http.get<any>(this.urlV2 + '/datasets/' + datasetId + '/teams/' + teamId + '/members', httpOptions);
  }

  getDatasetFiles(datasetId: number): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'}),
      withCredentials: this.withCredentials,
    };
    return this.http.get<any>(this.urlV2 + '/datasets/' + datasetId + '/files', httpOptions);
  }

  removeDatasetFile(datasetId: number, fileId: number): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'}),
      withCredentials: this.withCredentials,
    };
    return this.http.delete<any>(this.urlV2 + '/enterprise/datasets/' + datasetId + '/files/' + fileId, httpOptions);
  }

  public uploadDatasetFile(datasetId: number, data) {
    return this.http.post<any>(this.urlV2 + '/enterprise/datasets/' + datasetId + '/files', data, {
      withCredentials: this.withCredentials,
      reportProgress: true,
      observe: 'events',
    }).pipe(map((event) => {

        switch (event.type) {

          case HttpEventType.UploadProgress:
            const progress = Math.round(100 * event.loaded / event.total);
            return {status: 'progress', message: progress, uploaded: event.loaded};

          case HttpEventType.Response:
            return event.body;
          default:
            return `Unhandled event: ${event.type}`;
        }
      })
    );
  }

  getDatasetOtherFile(datasetId, fileId): Observable<any> {
    const httpOptions = {
      withCredentials: this.withCredentials,
      responseType: 'blob' as 'json',
      headers: new HttpHeaders({'Content-Type': 'application/json'}),
    };

    return this.http.get<any>(
      this.urlV2 + '/vendor/datasets/' + datasetId + '/files/' + fileId + '/file',
      httpOptions
    );
  }

  getDatasetOtherFileLink(datasetId, fileId): string {
    const token = localStorage.getItem('token');
    return this.urlV2 + '/vendor/datasets/' + datasetId + '/files/' + fileId + '/file?trustiiApiUserToken=' + token;
  }

  getForum(datasetId, forumId): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'}),
      withCredentials: this.withCredentials,
    };
    return this.http.get<any>(this.urlV2 + '/datasets/' + datasetId + '/forums/' + forumId, httpOptions);
  }

  getForums(datasetId: number, page: number = 1, perPage: number = 20): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'}),
      withCredentials: this.withCredentials,
    };

    const queryString = '?page=' + page + '&per_page=' + perPage;
    return this.http.get<any>(this.urlV2 + '/datasets/' + datasetId + '/forums' + queryString, httpOptions);
  }

  getForumMessages(datasetId, forumId, page: number = 1, perPage: number = 20): Observable<any> {
    const httpOptions = {
      withCredentials: this.withCredentials,
    };
    const queryString = '?page=' + page + '&per_page=' + perPage;
    return this.http.get<any>(this.urlV2 + '/datasets/' + datasetId + '/forums/' + forumId + '/messages' + queryString, httpOptions);
  }

  postForum(datasetId, payload): Observable<any> {
    const httpOptions = {
      withCredentials: this.withCredentials,
    };
    return this.http.post<any>(this.urlV2 + '/datasets/' + datasetId + '/forums', payload, httpOptions);
  }

  postForumMessage(datasetId, forumId, payload): Observable<any> {
    const httpOptions = {
      withCredentials: this.withCredentials,
    };
    return this.http.post<any>(this.urlV2 + '/datasets/' + datasetId + '/forums/' +
      forumId + '/messages', payload, httpOptions);
  }

  /**
   * url encode
   *
   * @param str string
   * @return string
   */
  urlEncode(str): string {
    str = (str + '').toString();

    return encodeURIComponent(str)
      .replace(/!/g, '%21')
      .replace(/'/g, '%27')
      .replace(/\(/g, '%28')
      .replace(/\)/g, '%29')
      .replace(/\*/g, '%2A')
      .replace(/%20/g, '+');
  }
}
